import { Partner, Society, StockPlan, User } from 'types';
import { isActivePartner } from 'modules/partners/utils/isActivePartner';
import {
  getDecimalScale,
  getFullAddress,
  getPartnerFDPercent,
  getPartnerPotentialFD,
  getSocietyValue,
} from 'utils/filters';
import getPartnerNDPercent from 'utils/getPartnerNDPercent';
import { getPartnersPrimaryEmails } from '../api/getPartnerPrimaryEmails';
import { getPartnerDetailsConfigByCorporateId } from './getPartnerDetailsConfigByCorporateId';

type GetPartnerListProps = {
  actualSociety: Society;
  partners: Partner[];
  plans: StockPlan[];
  user: User;
  userId: string | undefined;
  isReadOnly: boolean;
  isAdmin: boolean;
  hasAccessCaptable: boolean;
};

export type PartnerListNewData = {
  index: number;
  name: string;
  email: string;
  cif: string;
  shares: number;
  decimals: number | null;
  NDPercent: number;
  FDPercent: number;
  FDPotential: number;
  partner: Partner;
  society: Society;
  societyId: string;
  partnerNominalValue: number;
  partnerPremium: number;
  isActive: boolean;
  hasLink: boolean;
  userId: string;
  nationality: string;
  isReadOnly: boolean;
  isReadOnlyUser: boolean;
};

export type PartnerListCustomData = {
  NOMBRE: string;
  CIF: string;
  EMAIL: string;
  EMAILS_PRIMARIOS: string;
  NACIONALIDAD: string;
  PARTICIPACIONES: number;
  DIRECCION: string;
  '%ND': number;
  '%FD': number;
  '% Potential FD': number;
  Nominal: number;
  'Total Prima': number;
};

type PartnerListData = {
  newData: PartnerListNewData[];
  newCustomData: PartnerListCustomData[];
};

type MapPartnersData = {
  partners: Partner[];
  user: User;
  userId: string | undefined;
  hasAccessCaptable: boolean;
  isAdmin: boolean;
  actualSociety: Society;
  decimalScale: number | null;
  plans: StockPlan[];
  isReadOnly: boolean;
  primaryEmails?: any;
  showPartnerEditButton?: boolean;
};

const isPartnerAllowed = (
  partner: Partner,
  user: User,
  userId: string | undefined,
  hasAccessCaptable: boolean,
  isAdmin: boolean
): boolean =>
  userId || (!userId && !hasAccessCaptable && !isAdmin)
    ? user?._id === partner?.user ||
      partner?.otherUsers?.includes(user?._id) ||
      partner?.readOnlyUsers?.includes(user?._id) ||
      user?.usersWithAccess?.some(
        (currentUser: any) => partner?.user === currentUser?.['_id']
      )
    : true;

const parsePrimaryEmails = (partnerId: string, primaryEmails: any) => {
  const partnerEmails = primaryEmails?.find(
    (emails: { _id: string; userEmails: string[] }) =>
      emails?.['_id'] === partnerId
  );

  return partnerEmails?.userEmails?.join(', ') || '-';
};

const mapPartnersToNewData = ({
  partners,
  user,
  userId,
  hasAccessCaptable,
  isAdmin,
  actualSociety,
  decimalScale,
  plans,
  isReadOnly,
  showPartnerEditButton,
}: MapPartnersData) =>
  partners
    ?.filter((partner: Partner) => partner?.sharesCount?.actual >= 0)
    ?.filter((partner: Partner) =>
      isPartnerAllowed(partner, user, userId, hasAccessCaptable, isAdmin)
    )
    ?.map((partner: Partner) => ({
      index: partner?.partnerNumber,
      name: partner.name,
      email: partner.email,
      cif: partner?.cif || '-',
      shares: partner.sharesCount.actual,
      decimals: decimalScale,
      NDPercent: getPartnerNDPercent(partner, actualSociety),
      ADPercent: getPartnerFDPercent(partner, actualSociety),
      FDPercent: getPartnerPotentialFD(partner, actualSociety, plans),
      partner,
      society: actualSociety,
      societyId: actualSociety?._id,
      partnerNominalValue:
        partner?.sharesCount?.actual * actualSociety?.nominalValue || 0,
      // TODO: [AXA] - Check which calculation of Nominal is correct
      // V1 -> partnerNominalValue: parseFloat(
      //   getPartnerNominalValue(partner?.shares, actualSociety).toFixed(2)
      // ),
      // V2 -> partnerNominalValue:
      //   (actualSociety?.nominalValue || 0) * partner?.sharesCount?.actual,
      partnerPremium: partner.premium,
      // TODO: [AXA] - Check which calculation of Premium is correct
      // V1 -> partnerPremium: 0,
      // V2 -> partnerPremium: parseFloat(
      //   getPartnerPremium(partner?.shares, actualSociety).toFixed(2)
      // ),
      isActive: isActivePartner(partner),
      hasLink:
        partner?.user === user?._id ||
        partner?.otherUsers?.includes(user?._id) ||
        partner?.readOnlyUsers?.includes(user?._id) ||
        isAdmin,
      userId: user?.['_id'],
      nationality: partner?.nationality || '-',
      isReadOnly,
      isReadOnlyUser: partner?.readOnlyUsers?.includes(user?.['_id']),
      isEditButtonDisabled: !showPartnerEditButton && !isAdmin,
    }));

const mapPartnersToCustomData = ({
  actualSociety,
  partners,
  user,
  userId,
  hasAccessCaptable,
  isAdmin,
  primaryEmails,
  plans,
}: MapPartnersData) =>
  partners
    ?.filter((partner: Partner) => partner?.sharesCount?.actual >= 0)
    ?.filter((partner: Partner) =>
      isPartnerAllowed(partner, user, userId, hasAccessCaptable, isAdmin)
    )
    ?.map((partner: Partner) => ({
      NOMBRE: partner.name,
      CIF: partner?.cif,
      EMAIL: partner?.email,
      EMAILS_PRIMARIOS: parsePrimaryEmails(partner?._id, primaryEmails),
      NACIONALIDAD: partner?.nationality || '-',
      PARTICIPACIONES: partner.sharesCount.actual,
      DIRECCION: getFullAddress(partner?.address),
      '%ND': getPartnerNDPercent(partner, actualSociety),
      '%FD': getPartnerFDPercent(partner, actualSociety),
      '% Potential FD': getPartnerPotentialFD(partner, actualSociety, plans),
      Nominal: partner.nominalValue,
      'Total Prima': partner.premium,
    }));

export const getPartnerList = async (
  props: GetPartnerListProps
): Promise<PartnerListData> => {
  try {
    const {
      actualSociety,
      partners,
      plans,
      user,
      userId,
      isReadOnly,
      isAdmin,
      hasAccessCaptable,
    } = props;

    if (!actualSociety || !partners?.length) {
      return { newData: [], newCustomData: [] };
    }

    if (!partners?.length) {
      return { newData: [], newCustomData: [] };
    }

    const societyValue = getSocietyValue(actualSociety);
    const decimalScale = societyValue
      ? getDecimalScale(societyValue.value)
      : actualSociety?.configuration?.decimalScale || 2;

    const primaryEmails = await getPartnersPrimaryEmails(actualSociety?._id);
    const { showPartnerEditButton } =
      getPartnerDetailsConfigByCorporateId(actualSociety);

    const newData = mapPartnersToNewData({
      actualSociety,
      partners,
      user,
      userId,
      hasAccessCaptable,
      isAdmin,
      decimalScale,
      plans,
      isReadOnly,
      showPartnerEditButton,
    } as MapPartnersData);

    const newCustomData = mapPartnersToCustomData({
      actualSociety,
      partners,
      user,
      userId,
      hasAccessCaptable,
      isAdmin,
      primaryEmails,
      plans,
    } as MapPartnersData);

    return { newData, newCustomData } as any;
  } catch (error) {
    return { newData: [], newCustomData: [] };
  }
};
